// Normalizers for the various question payload shapes returned by the API.
// Every place that renders a question should go through these helpers so the
// exam library, question bank and modals all agree on what a question shows.

const TEXT_KEYS = ['questionText', 'question', 'prompt', 'statement', 'text', 'content', 'body'];
const IMAGE_KEYS = ['imageUrl', 'image_url', 'image', 'diagramUrl', 'diagram_url', 'mediaUrl', 'figureUrl', 'image_link'];

// A GCS object path may arrive already percent-encoded (spaces as %20); decode
// it first so it is not encoded twice.
function decodePath(path) {
  try {
    return decodeURIComponent(path);
  } catch (_) {
    return path;
  }
}

export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Firebase download URLs (https://firebasestorage.googleapis.com/v0/b/…?alt=media,
  // what the ETL now stores) and any other URL pass through untouched below.

  // Convert a public GCS URL (https://storage.googleapis.com/{bucket}/{path}) to a
  // Firebase Storage download URL. A URL with a query string is a signed URL whose
  // signature covers that exact host and path, so it is left alone.
  const gcsMatch = trimmed.match(/^https:\/\/storage\.googleapis\.com\/([^/?#]+)\/([^?#]+)$/);
  if (gcsMatch) {
    const [, bucket, path] = gcsMatch;
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(decodePath(path))}?alt=media`;
  }

  // Convert gs://{bucket}/{path} URL to Firebase Storage download URL
  const gsMatch = trimmed.match(/^gs:\/\/([^/]+)\/(.+)$/);
  if (gsMatch) {
    const [, bucket, path] = gsMatch;
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
  }

  return trimmed;
}

export function getQuestionText(question) {
  const value = TEXT_KEYS
    .map((key) => question?.[key])
    .find((candidate) => typeof candidate === 'string' && candidate.trim());
  return value?.trim() || 'Untitled question';
}

export function getQuestionImageUrl(question) {
  const value = IMAGE_KEYS
    .map((key) => question?.[key])
    .find((candidate) => typeof candidate === 'string' && candidate.trim());
  return normalizeImageUrl(value);
}

// The crop of the diagram as printed in the uploaded paper. Present when the
// ETL redrew the diagram (`imageGenerated`), so reviewers can compare the two.
export function getSourceImageUrl(question) {
  return normalizeImageUrl(question?.sourceImageUrl);
}

export function getQuestionLabel(question, index, maxLength = 160) {
  const text = getQuestionText(question);
  if (text === 'Untitled question') return `Question ${index + 1}`;
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
}

export function getQuestionOptions(question) {
  const raw = question?.options || question?.choices || question?.answerOptions || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((option, index) => {
      if (typeof option === 'string') return { label: option, id: `${index}`, index };
      const label = option?.text || option?.optionText || option?.label || option?.content || option?.value || option?.title || '';
      const rawImg = IMAGE_KEYS
        .map((key) => option?.[key])
        .find((candidate) => typeof candidate === 'string' && candidate.trim());
      const imageUrl = normalizeImageUrl(rawImg);
      if (!label && !imageUrl) return null;
      return { label, imageUrl, id: option?.id ?? option?.optionId ?? `${index}`, orderIndex: option?.orderIndex, index };
    })
    .filter(Boolean);
}

function getAnswerEntries(question) {
  const raw = question?.answer || question?.answers || question?.correctAnswers || question?.correct_answers || [];
  return Array.isArray(raw) ? raw : [];
}

function isCorrectEntry(entry) {
  return entry?.isCorrect === true || entry?.correct === true;
}

// The option (as returned by getQuestionOptions) an answer entry points at.
function optionForEntry(entry, options) {
  let match = null;
  if (entry?.optionId != null) {
    match = options.find((option) => option.id === entry.optionId)
      || options.find((option) => option.orderIndex != null && option.orderIndex === entry.optionId)
      || options[entry.optionId];
  }
  if (!match && entry?.optionIndex != null) match = options[entry.optionIndex];
  return match || null;
}

// Set of option ids (as returned by getQuestionOptions) marked as correct.
export function getCorrectOptionIds(question, options) {
  const ids = new Set();
  getAnswerEntries(question)
    .filter(isCorrectEntry)
    .forEach((entry) => {
      const match = optionForEntry(entry, options);
      if (match) ids.add(match.id);
    });
  if (!ids.size) {
    const index = [question?.correctIndex, question?.correct_index].find(Number.isInteger);
    if (index != null && options[index]) ids.add(options[index].id);
  }
  return ids;
}

// Text fallback for when the correct answer cannot be mapped onto an option.
export function getCorrectAnswerText(question, options) {
  const correctIds = getCorrectOptionIds(question, options);
  if (correctIds.size) {
    return options.filter((option) => correctIds.has(option.id)).map((option) => option.label).join(', ');
  }
  const direct = ['correctAnswer', 'correct_answer', 'correctAnswerText']
    .map((key) => question?.[key])
    .find((value) => typeof value === 'string' && value.trim());
  if (direct) return direct.trim();
  const entryLabels = getAnswerEntries(question)
    .filter((entry) => entry?.isCorrect === true || entry?.correct === true)
    .map((entry) => entry?.text || entry?.value)
    .filter(Boolean);
  return entryLabels.join(', ');
}

function getDirectExplanation(question) {
  const direct = ['explanation', 'rationale', 'answerExplanation', 'answer_explanation', 'solution']
    .map((key) => question?.[key])
    .find((value) => typeof value === 'string' && value.trim());
  return direct ? direct.trim() : '';
}

export function getQuestionExplanation(question) {
  const direct = getDirectExplanation(question);
  if (direct) return direct;
  const entry = getAnswerEntries(question).find((item) => typeof item?.explanation === 'string' && item.explanation.trim());
  return entry ? entry.explanation.trim() : '';
}

export function getQuestionTags(question) {
  return Array.isArray(question?.tags) ? question.tags.filter(Boolean) : [];
}

// The explanation written for one option being the correct answer: the one on
// that option's correct answer entry, else a question-level explanation.
// Another option's explanation never counts — it argues for a different answer.
export function getOptionExplanation(question, options, optionId) {
  const entry = getAnswerEntries(question)
    .filter(isCorrectEntry)
    .find((item) => optionForEntry(item, options)?.id === optionId);
  if (typeof entry?.explanation === 'string' && entry.explanation.trim()) return entry.explanation.trim();
  if (entry || getCorrectOptionIds(question, options).has(optionId)) return getDirectExplanation(question);
  return '';
}
